import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form, Checkbox, Dropdown } from 'semantic-ui-react';
import { Input } from '../../../lib/custom-ui';

import styles from '../SettingDashboard.module.scss';

const ComponentType1Form = React.memo(
  ({
    devices,
    factories,
    items,
    data,
    isSubmitting,
    onChangeField,
    onChangeEmptyField,
    isError,
  }) => {
    const [t] = useTranslation();

    const devicesOptions = [];
    devices.forEach((item) => {
      devicesOptions.push({ key: item.id, value: item.id, text: item.name });
    });

    const factoriesOptions = [];
    factories.forEach((item) => {
      factoriesOptions.push({ key: item.id, value: item.id, text: item.name });
    });

    const itemsOptions = [];
    items.forEach((item) => {
      itemsOptions.push({ key: item.id, value: item.itemName, text: item.name });
    });

    const descriptionField = useRef(null);
    const titleField = useRef(null);
    const intervalField = useRef(null);
    const unitNameField = useRef(null);
    const decimalPointField = useRef(null);

    // const positionOptions = [
    //   { key: 7, value: 7, text: 7 },
    //   { key: 8, value: 8, text: 8 },
    // ];
    const positionOptions = Array.from({ length: 100 }, (_, i) => {
      return { key: i + 1, value: i + 1, text: i + 1 };
    });

    const dataTypeOptions = [
      { key: 1, value: 'yearly', text: t('common.components.yearly') },
      { key: 2, value: 'monthly', text: t('common.components.monthly') },
      { key: 3, value: 'weekly', text: t('common.components.weekly') },
      { key: 4, value: 'daily', text: t('common.components.daily') },
    ];

    // const timeOptions = [
    //   { key: 1, value: 'lastyearly', text: t('common.components.totalLastYear') },
    //   { key: 2, value: 'yearly', text: t('common.components.totalThisYear') },
    //   { key: 3, value: 'lastmonthly', text: t('common.components.totalLastMonth') },
    //   { key: 4, value: 'monthly', text: t('common.components.totalThisMonth') },
    //   { key: 5, value: 'lastweekly', text: t('common.components.totalLastWeek') },
    //   { key: 6, value: 'weekly', text: t('common.components.totalThisWeek') },
    //   { key: 7, value: 'lastdaily', text: t('common.components.totalLastDay') },
    //   { key: 8, value: 'daily', text: t('common.components.totalToday') },
    // ];
    let defaultTimeOptions = [];
    switch (data.dataType) {
      case 'daily':
        defaultTimeOptions = [
          { key: 7, value: '-1', text: t('common.components.totalLastDay') },
          { key: 8, value: '0', text: t('common.components.totalToday') },
        ];
        break;
      case 'weekly':
        defaultTimeOptions = [
          { key: 5, value: '-1', text: t('common.components.totalLastWeek') },
          { key: 6, value: '0', text: t('common.components.totalThisWeek') },
        ];
        break;
      case 'monthly':
        defaultTimeOptions = [
          { key: 3, value: '-1', text: t('common.components.totalLastMonth') },
          { key: 4, value: '0', text: t('common.components.totalThisMonth') },
        ];
        break;
      case 'yearly':
        defaultTimeOptions = [
          { key: 1, value: '-1', text: t('common.components.totalLastYear') },
          { key: 2, value: '0', text: t('common.components.totalThisYear') },
        ];
        break;
      default:
    }
    const [timeOptions, setTimeOptions] = useState(defaultTimeOptions);

    const handleFieldDataTypeChange = useCallback(
      (e, dataValue) => {
        onChangeField(e, dataValue);
        let tOptions = [];
        switch (dataValue.value) {
          case 'daily':
            tOptions = [
              { key: 7, value: '-1', text: t('common.components.totalLastDay') },
              { key: 8, value: '0', text: t('common.components.totalToday') },
            ];
            break;
          case 'weekly':
            tOptions = [
              { key: 5, value: '-1', text: t('common.components.totalLastWeek') },
              { key: 6, value: '0', text: t('common.components.totalThisWeek') },
            ];
            break;
          case 'monthly':
            tOptions = [
              { key: 3, value: '-1', text: t('common.components.totalLastMonth') },
              { key: 4, value: '0', text: t('common.components.totalThisMonth') },
            ];
            break;
          case 'yearly':
            tOptions = [
              { key: 1, value: '-1', text: t('common.components.totalLastYear') },
              { key: 2, value: '0', text: t('common.components.totalThisYear') },
            ];
            break;
          default:
        }
        setTimeOptions(tOptions);
      },
      [onChangeField, t],
    );

    const [positionError, setPositionError] = useState(false);
    const [dataTypeError, setDataTypeError] = useState(false);
    const [timeError, setTimeError] = useState(false);
    const [factoryIdsError, setFactoryIdsError] = useState(false);
    const [deviceIdsError, setDeviceIdsError] = useState(false);
    const [itemNameError, setItemNameError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [unitNameError, setUnitNameError] = useState(false);
    const [titleError, setTitleError] = useState(false);
    const [decimalPointError, setDecimalPointError] = useState(false);
    const [intervalError, setIntervalError] = useState(false);
    useEffect(() => {
      setPositionError(isError.indexOf('position') !== -1);
      setDataTypeError(isError.indexOf('dataType') !== -1);
      setTimeError(isError.indexOf('time') !== -1);
      setFactoryIdsError(isError.indexOf('factoryIds') !== -1);
      setDeviceIdsError(isError.indexOf('deviceIds') !== -1);
      setItemNameError(isError.indexOf('itemName') !== -1);
      setDescriptionError(isError.indexOf('description') !== -1);
      setUnitNameError(isError.indexOf('unitName') !== -1);
      setTitleError(isError.indexOf('title') !== -1);
      setDecimalPointError(isError.indexOf('decimalPoint') !== -1);
      setIntervalError(isError.indexOf('interval') !== -1);
    }, [isError]);

    return (
      <>
        <Form.Field required error={positionError} onFocus={() => setPositionError(false)}>
          <label htmlFor="position">{t('common.components.position')}</label>
          <Form.Select
            name="position"
            className={styles.field}
            placeholder={t('common.selectPosition')}
            fluid
            selection
            value={data.position}
            options={positionOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={dataTypeError} onFocus={() => setDataTypeError(false)}>
          <label htmlFor="dataType">{t('common.components.dataType')}</label>
          <Form.Select
            name="dataType"
            className={styles.field}
            placeholder={t('common.selectDataType')}
            fluid
            selection
            value={data.dataType}
            options={dataTypeOptions}
            onChange={handleFieldDataTypeChange}
          />
        </Form.Field>
        <Form.Field required error={timeError} onFocus={() => setTimeError(false)}>
          <label htmlFor="time">{t('common.components.time')}</label>
          <Form.Select
            name="time"
            className={styles.field}
            placeholder={t('common.selectTime')}
            fluid
            selection
            value={data.time}
            options={timeOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={factoryIdsError} onFocus={() => setFactoryIdsError(false)}>
          <label htmlFor="factoryIds">{t('common.components.factories')}</label>
          <Dropdown
            multiple
            name="factoryIds"
            className={styles.field}
            placeholder={t('common.selectFactory')}
            search
            fluid
            selection
            value={data.factoryIds}
            options={factoriesOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={deviceIdsError} onFocus={() => setDeviceIdsError(false)}>
          <label htmlFor="deviceIds">{t('common.components.devices')}</label>
          <Dropdown
            multiple
            name="deviceIds"
            className={styles.field}
            placeholder={t('common.selectDevice')}
            search
            fluid
            selection
            value={data.deviceIds}
            options={devicesOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={itemNameError} onFocus={() => setItemNameError(false)}>
          <label htmlFor="itemName">{t('common.components.items')}</label>
          <Form.Select
            name="itemName"
            className={styles.field}
            placeholder={t('common.selectItem')}
            search
            fluid
            selection
            value={data.itemName}
            options={itemsOptions}
            onChange={onChangeField}
          />
        </Form.Field>
        {/* <Form.Field>
          <Label>{t('common.components.dataType')}</Label>
          <Form.Select
            name="dataType"
            className={styles.field}
            placeholder={t('common.selectDataType')}
            search
            fluid
            selection
            value={data.dataType}
            options={dataTypeOptions}
            onChange={onChangeField}
          />
        </Form.Field> */}
        <Form.Field required error={descriptionError} onFocus={() => setDescriptionError(false)}>
          <label htmlFor="description">{t('common.components.description')}</label>
          <Input
            fluid
            ref={descriptionField}
            name="description"
            value={data.description}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field>
          <Checkbox
            toggle
            label={t('common.components.empty')}
            name="empty"
            defaultChecked={data.empty === true}
            readOnly={isSubmitting}
            onChange={onChangeEmptyField}
          />
        </Form.Field>
        <Form.Field required error={unitNameError} onFocus={() => setUnitNameError(false)}>
          <label htmlFor="unitName">{t('common.components.unitName')}</label>
          <Input
            fluid
            ref={unitNameField}
            name="unitName"
            value={data.unitName}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={titleError} onFocus={() => setTitleError(false)}>
          <label htmlFor="title">{t('common.components.title')}</label>
          <Input
            fluid
            ref={titleField}
            name="title"
            value={data.title}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={decimalPointError} onFocus={() => setDecimalPointError(false)}>
          <label htmlFor="decimalPoint">{t('common.components.decimalPoint')}</label>
          <Input
            fluid
            type="number"
            ref={decimalPointField}
            name="decimalPoint"
            value={data.decimalPoint}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
        <Form.Field required error={intervalError} onFocus={() => setIntervalError(false)}>
          <label htmlFor="interval">{t('common.components.interval')}</label>
          <Input
            fluid
            type="number"
            ref={intervalField}
            name="interval"
            value={data.interval}
            readOnly={isSubmitting}
            className={styles.field}
            onChange={onChangeField}
          />
        </Form.Field>
      </>
    );
  },
);

ComponentType1Form.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.any).isRequired,
  factories: PropTypes.arrayOf(PropTypes.any).isRequired,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onChangeEmptyField: PropTypes.func.isRequired,
  isError: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default ComponentType1Form;
